import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Receipt, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './PropertyTaxCalculatorPage/components/CalculationForm';
import ResultsOverview from './PropertyTaxCalculatorPage/components/ResultsOverview';
import TaxBreakdown from './PropertyTaxCalculatorPage/components/TaxBreakdown';
import QuickAdjustmentSidebar from './PropertyTaxCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './PropertyTaxCalculatorPage/components/ComparisonSidebar';
import TaxInfoSidebar from './PropertyTaxCalculatorPage/components/TaxInfoSidebar';
import InfoContent from './PropertyTaxCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  propertyType: 'apartment', // тип имущества: квартира, дом, земля и т.д.
  cadastralValue: '5000000', // кадастровая стоимость
  area: '50', // площадь объекта
  ownershipShare: '1', // доля владения в виде десятичной дроби (1 = 100%)
  ownershipDuration: '12', // продолжительность владения в месяцах в течение налогового периода
  region: 'moscow', // регион (влияет на налоговую ставку)
  hasPrivileges: false, // наличие налоговых льгот
  privilegeType: 'none', // тип льготы
  personalData: {
    isPensioner: false, // пенсионер
    isVeteran: false, // ветеран боевых действий
    isDisabled: false, // инвалид 1 и 2 группы
    isHero: false, // герой РФ
    isMilitaryPerson: false, // военнослужащий
  },
  propertyData: {
    constructionYear: '2010', // год постройки
    registrationDate: new Date().toISOString().split('T')[0], // дата регистрации права собственности
    isHistorical: false, // исторический объект
    isCulturalHeritage: false, // объект культурного наследия
  },
  taxYear: new Date().getFullYear() - 1, // налоговый период (год)
  useDeduction: true, // использовать налоговый вычет
  deductionType: 'standard', // тип вычета: стандартный, расширенный...
  useReducingCoefficient: true, // использовать понижающий коэффициент для новостроек
  includeDepreciation: false, // учитывать износ объекта
  depreciationPercent: '0', // процент износа объекта
};

// Определение типов имущества
export const propertyTypes = [
  { id: 'apartment', name: 'Квартира' },
  { id: 'room', name: 'Комната' },
  { id: 'house', name: 'Жилой дом' },
  { id: 'cottage', name: 'Дача/садовый дом' },
  { id: 'garage', name: 'Гараж/машино-место' },
  { id: 'office', name: 'Офис/торговое помещение' },
  { id: 'land', name: 'Земельный участок' },
  { id: 'other', name: 'Иное имущество' }
];

// Определение регионов и ставок налога
export const regionTaxRates = [
  { 
    id: 'moscow', 
    name: 'Москва', 
    rates: {
      apartment: 0.1, // квартиры стоимостью до 10 млн. руб.
      apartmentHigh: 0.15, // квартиры стоимостью от 10 до 20 млн. руб.
      apartmentPremium: 0.2, // квартиры стоимостью от 20 до 50 млн. руб.
      apartmentLuxury: 0.3, // квартиры стоимостью от 50 до 300 млн. руб.
      apartmentUltraLuxury: 0.5, // квартиры стоимостью свыше 300 млн. руб.
      house: 0.1, // жилые дома
      office: 2, // офисы, торговые центры (базовая ставка)
      garage: 0.1, // гаражи, машино-места
      other: 0.5, // прочее имущество
      land: 0.3, // земельные участки (стандартная ставка)
    },
    deductions: {
      apartment: 20, // кв.м. для квартир
      room: 10, // кв.м. для комнат
      house: 50, // кв.м. для домов
      cottage: 50, // кв.м. для дач
    },
  },
  { 
    id: 'saint_petersburg', 
    name: 'Санкт-Петербург', 
    rates: {
      apartment: 0.1,
      apartmentHigh: 0.15,
      apartmentPremium: 0.2,
      apartmentLuxury: 0.3,
      apartmentUltraLuxury: 0.5,
      house: 0.1,
      office: 2,
      garage: 0.1,
      other: 0.5,
      land: 0.3,
    },
    deductions: {
      apartment: 20,
      room: 10,
      house: 50,
      cottage: 50,
    },
  },
  { 
    id: 'other_region', 
    name: 'Другие регионы', 
    rates: {
      apartment: 0.1,
      apartmentHigh: 0.2,
      apartmentPremium: 0.3,
      apartmentLuxury: 0.3,
      apartmentUltraLuxury: 0.3,
      house: 0.1,
      office: 2,
      garage: 0.1,
      other: 0.5,
      land: 0.3,
    },
    deductions: {
      apartment: 20,
      room: 10,
      house: 50,
      cottage: 50,
    },
  }
];

// Определение типов налоговых льгот
export const privilegeTypes = [
  { id: 'none', name: 'Без льгот' },
  { id: 'pensioner', name: 'Пенсионер' },
  { id: 'veteran', name: 'Ветеран боевых действий' },
  { id: 'disabled', name: 'Инвалид I и II группы' },
  { id: 'hero', name: 'Герой РФ, СССР' },
  { id: 'chernobyl', name: 'Ликвидатор аварии на ЧАЭС' },
  { id: 'military', name: 'Военнослужащий' },
  { id: 'other', name: 'Иные льготы' }
];

/**
 * PropertyTaxCalculatorPage Component - Калькулятор для расчета налога на имущество с различными параметрами и льготами
 */
const PropertyTaxCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('property-tax-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор налога на имущество | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор налога на имущество физических лиц в России: расчет налога на квартиру, дом, землю по кадастровой стоимости с учетом всех налоговых льгот, вычетов и особенностей региона.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [taxBreakdown, setTaxBreakdown] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedPropertyTaxCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved property tax calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'apartment':
          newState.propertyType = 'apartment';
          break;
        case 'house':
          newState.propertyType = 'house';
          break;
        case 'land':
          newState.propertyType = 'land';
          break;
        case 'commercial':
          newState.propertyType = 'office';
          break;
        case 'with-privileges':
          newState.hasPrivileges = true;
          newState.privilegeType = 'pensioner';
          break;
        case 'partial-ownership':
          newState.ownershipShare = '0.5';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setTaxBreakdown([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета налога в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Расчет налога на ${getPropertyTypeName(formData.propertyType)}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedPropertyTaxCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedCalculations);
    localStorage.setItem('savedPropertyTaxCalculations', JSON.stringify(updatedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      // Пересчитаем детали для визуализации
      generateTaxBreakdown(calcToLoad.results);
    }
  };

  // Вспомогательная функция для получения названия типа имущества
  const getPropertyTypeName = (typeId) => {
    const foundType = propertyTypes.find(type => type.id === typeId);
    return foundType ? foundType.name.toLowerCase() : 'имущество';
  };

  // Генерация подробной разбивки налога
  const generateTaxBreakdown = (results) => {
    if (!results) return;

    // Здесь будет логика создания разбивки налога для визуализации
    // В качестве примера добавим базовую разбивку
    const breakdown = [
      {
        name: 'Базовый расчет налога',
        value: results.baseTax,
        description: 'Кадастровая стоимость × Налоговая ставка'
      }
    ];

    // Добавляем вычеты, если они применяются
    if (results.deduction > 0) {
      breakdown.push({
        name: 'Налоговый вычет',
        value: -results.deduction,
        description: `Вычет на ${results.deductionArea} кв.м.`
      });
    }

    // Добавляем понижающий коэффициент, если он применяется
    if (results.reducingCoefficientValue < 1) {
      breakdown.push({
        name: 'Понижающий коэффициент',
        value: -(results.baseTax - results.baseTax * results.reducingCoefficientValue),
        description: `Понижающий коэффициент ${results.reducingCoefficientValue} для нового имущества`
      });
    }

    // Добавляем льготы, если они применяются
    if (results.privilegeDiscount > 0) {
      breakdown.push({
        name: 'Налоговая льгота',
        value: -results.privilegeDiscount,
        description: `Льгота для категории "${results.privilegeDescription}"`
      });
    }

    // Добавляем корректировку по доле владения
    if (results.ownershipShareFactor < 1) {
      breakdown.push({
        name: 'Корректировка по доле владения',
        value: -(results.taxBeforeShare - results.finalTax),
        description: `С учетом доли владения ${results.ownershipShareFactor * 100}%`
      });
    }

    // Добавляем корректировку по периоду владения
    if (results.ownershipPeriodFactor < 1) {
      breakdown.push({
        name: 'Корректировка по периоду владения',
        value: -(results.taxBeforePeriod - results.finalTax),
        description: `С учетом периода владения ${results.ownershipPeriodFactor * 100}% года`
      });
    }

    setTaxBreakdown(breakdown);
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Receipt className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор налога на имущество'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              generateTaxBreakdown={generateTaxBreakdown}
              propertyTypes={propertyTypes}
              regionTaxRates={regionTaxRates}
              privilegeTypes={privilegeTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <TaxBreakdown 
                  breakdown={taxBreakdown} 
                  results={results}
                />
              </>
            )}

            {/* --- Информационный контент о налоге на имущество --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              propertyTypes={propertyTypes}
              regionTaxRates={regionTaxRates}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
              propertyTypes={propertyTypes}
            />

            {/* --- Панель информации о налоге --- */}
            <TaxInfoSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default PropertyTaxCalculatorPage;